"use client";
import { ReactFlowProvider } from "@xyflow/react";
import {
  Account,
  BuiltInApp,
  RawCanvas,
  Session,
  Toggles,
} from "@zapier/canvas-state";
import { CurrentAccountIdProvider } from "@zapier/identity";
import dynamic from "next/dynamic";
import { useEmitUserInteractionEvents } from "@zapier/observability";

import { Canvas } from "../components/Canvas";
import { CanvasHydrator } from "../components/CanvasHydrator";
import { CanvasBackground } from "../components/CanvasBackground";
import { CanvasComposerHydrator } from "../components/CanvasComposerHydrator";
import {
  useRegisterComposerSendButtonEvent,
  useRegisterComposerEnterKeyEvent,
} from "../hooks/useRegisterEventEmitter";
import { useSavedChatState } from "../hooks/useSavedChatState";
import { useExecuteAiPrompt } from "../hooks/useExecuteAiPrompt";

// Import BuilderNav as a client-only component to solve hydration issues
const BuilderNav = dynamic(
  () => import("../components/BuilderNav").then((mod) => mod.BuilderNav),
  { ssr: false },
);

type Props = {
  canvas: RawCanvas;
  chatbotsApp: BuiltInApp;
  tablesApp: BuiltInApp;
  interfacesApp: BuiltInApp;
  agentsApp: BuiltInApp;
  session?: Session;
  account?: Account;
  toggles: Toggles;
  prompt?: string;
  chatId?: string;
};

// 'cuz CanvasComposerHydrator needs to be a parent of useExecuteAiPrompt hook
function ExecuteAiPrompt({ prompt }: { prompt?: string }) {
  useExecuteAiPrompt(prompt ?? "");
  return null;
}

export function AiPage({
  canvas,
  chatbotsApp,
  tablesApp,
  interfacesApp,
  agentsApp,
  toggles,
  account,
  session,
  prompt,
  chatId,
}: Props) {
  const { emitEventOnComposerMessageSent } = useEmitUserInteractionEvents({
    canvasId: canvas.id,
    sessionId: session?.id,
    currentAccountId: account?.id,
  });

  // Register both click and keyboard events
  useRegisterComposerSendButtonEvent(emitEventOnComposerMessageSent);
  useRegisterComposerEnterKeyEvent(emitEventOnComposerMessageSent);

  // Use the hook to load saved chat state if chatId is provided
  useSavedChatState(chatId);

  return (
    <CurrentAccountIdProvider>
      <ReactFlowProvider>
        <CanvasHydrator
          canvas={canvas}
          chatbotsApp={chatbotsApp}
          tablesApp={tablesApp}
          interfacesApp={interfacesApp}
          agentsApp={agentsApp}
          toggles={toggles}
          account={account}
          session={session}
          prompt={prompt}
          isAiPage={true}
        >
          <CanvasComposerHydrator>
            <ExecuteAiPrompt prompt={prompt} />
            <BuilderNav>
              <Canvas>
                <CanvasBackground />
              </Canvas>
            </BuilderNav>
          </CanvasComposerHydrator>
        </CanvasHydrator>
      </ReactFlowProvider>
    </CurrentAccountIdProvider>
  );
}
